import React, { useState, useEffect } from "react";
import { Box, Divider, FormControlLabel, Paper, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";

import EnergyDataChart from "./EnergyDataChart";
import EnergySavingsSlidingMenuMobile from "./EnergySavingsSlidingMenuMobile";

const leftCardStyles = {
  boxShadow: "0px 1px 14px 0px #00071624",
  padding: "16px",
};

const getRightCardStyles = (isSmallScreen: boolean) => ({
  height: "227px",
  boxShadow: "0px 1px 14px 0px #00071624",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  width: isSmallScreen ? "50%" : "100%",
});

const titleStyles = {
  fontSize: "40px",
  fontWeight: "700",
  color: "primary.main",
};

const subtitleStyles = {
  fontSize: "14px",
  fontWeight: "700",
  color: "text.secondary",
  textTransform: "uppercase",
};

export default function EnergyDataCard({ kpiCardData, setEnableFetchProjectDashboardKPICardData, projectName, siteName }: any) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));

  // Store energyType in state only (no localStorage)
  const [energyType, setEnergyType] = useState("KWH");
  const [carbonReductionSavings, setCarbonReductionSavings] = useState<number>(0);
  const [energySavings, setEnergySavings] = useState<number>(0);

  // Update values when kpiCardData or energyType changes
  // Update values when kpiCardData or energyType changes
  useEffect(() => {
    const rawCarbonReductionSavings = parseFloat(String(kpiCardData?.energy_savings_data?.carbon_reduction_savings)) || 0;

    // const rawEnergySavings = parseFloat(String(kpiCardData?.energy_savings_data?.energy_savings)) || 0;
    const rawEnergySavings = parseFloat(String(kpiCardData?.energy_savings_data?.total_savings)) || 0;
    const rawEnergySavingsTherms = parseFloat(String(kpiCardData?.energy_savings_data?.total_savings_therms)) || 0;

    // Ensure values have at most 2 decimal places and remain numbers
    const formattedCarbonReductionSavings = parseFloat(rawCarbonReductionSavings.toFixed(2));
    const formattedEnergySavings = parseFloat(rawEnergySavings.toFixed(2));
    const formattedEnergySavingsTherms = parseFloat(rawEnergySavingsTherms.toFixed(2));

    setCarbonReductionSavings(formattedCarbonReductionSavings);
    setEnergySavings(energyType === "THERMS" ? formattedEnergySavingsTherms : formattedEnergySavings);
  }, [energyType, kpiCardData]);

  // Debugging: Log changes
  useEffect(() => {
    console.log("Updated values:", { carbonReductionSavings, energySavings, energyType });
  }, [carbonReductionSavings, energySavings, energyType]);

  // Toggle energy type
  const handleChangeEnergyType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnergyType(event.target.checked ? "THERMS" : "KWH"); // Directly update state
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h2">Energy data</Typography>
      <Grid
        container
        spacing={3}
        sx={{
          marginTop: isSmallScreen ? "8px" : "16px",
        }}
      >
        {isSmallScreen && <EnergyDataDemandResponse isSmallScreen={isSmallScreen} kpiCardData={kpiCardData} energySavings={energySavings} />}
        <Grid size={{ xs: 12, lg: 10 }}>
          <Paper sx={leftCardStyles}>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h3">year to date</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body1">{"kWh"}</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={energyType === "THERMS"}
                      sx={{
                        "& .MuiSwitch-thumb": {
                          color: "#193561",
                        },
                      }}
                      onChange={handleChangeEnergyType}
                    />
                  }
                  label="therms"
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
              {!isSmallScreen ? (
                <Grid container spacing={3} sx={{ marginTop: "10px", width: "100%" }}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Box>
                      <Typography sx={titleStyles} align="center">
                        {Number(energySavings || 0).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                      <Typography sx={subtitleStyles} align="center">
                        energy savings
                      </Typography>
                    </Box>
                  </Grid>
                  <Divider orientation="vertical" flexItem />
                  <Grid size={{ xs: 12, md: 2 }}>
                    <Box>
                      <Typography sx={titleStyles} align="center">
                        {carbonReductionSavings}
                      </Typography>
                      <Typography sx={subtitleStyles} align="center">
                        carbon reduction
                        <br />
                        savings (mTons)
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <EnergySavingsSlidingMenuMobile energySavings={energySavings} carbonReductionSavings={carbonReductionSavings} />
              )}
            </Box>
            <Box sx={{ marginTop: "20px" }} />
            <EnergyDataChart kpiCardData={kpiCardData} projectName={projectName} siteName={siteName} energySavings={energySavings} energyType={energyType} />
          </Paper>
        </Grid>
        {!isSmallScreen && <EnergyDataDemandResponse isSmallScreen={isSmallScreen} kpiCardData={kpiCardData} energySavings={energySavings} />}
      </Grid>
    </Box>
  );
}

function EnergyDataDemandResponse({ isSmallScreen, kpiCardData, energySavings }: any) {
  return (
    <Grid size={{ xs: 12, lg: 2 }}>
      <Box sx={{ display: "flex", flexDirection: isSmallScreen ? "row" : "column", gap: "22px" }}>
        <Paper sx={getRightCardStyles(isSmallScreen)}>
          <Typography sx={titleStyles}>
            {Number(kpiCardData?.electric_demand_last_15_min || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>
          <Typography sx={subtitleStyles} align="center">
            electric demand
            <br /> last 15 min (kw)
          </Typography>
        </Paper>

        <Paper sx={getRightCardStyles(isSmallScreen)}>
          <Typography sx={titleStyles}>{kpiCardData?.sites_with_demand_response}</Typography>
          <Typography sx={subtitleStyles} align="center">
            sites with <br />
            demand response
          </Typography>
        </Paper>
      </Box>
    </Grid>
  );
}
